import { useState, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Collapse,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useRolePermissions, useUpdateRolePermissions } from '@/hooks/identity/useIdentity';
import { PermissionGroup, Permission, PermissionGrant } from '@/types/identity/Identity';
import { useDebounce } from '@/hooks/useDebounce';
import { GrantedPermissionsOverview } from '@/components/GrantedPermissionsOverview';

interface PermissionTreeProps {
  /** Role name to load/save permissions for */
  roleName: string;
  /**
   * Fires when the pending-change state flips, so the page's Save button can disable itself
   * when there is nothing to save — the standard detail-view behaviour.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

export interface PermissionTreeHandle {
  /** Save the pending grants. Rejects on failure — outcome reporting is the page's (Save button + error banner). */
  save: () => Promise<void>;
  /** Discard the pending grants: back to the server's saved state (the page's Cancel). */
  revert: () => void;
  hasChanges: boolean;
}

export const PermissionTree = forwardRef<PermissionTreeHandle, PermissionTreeProps>(
  ({ roleName, onDirtyChange }, ref) => {
    const { data, isLoading, error } = useRolePermissions(roleName);
    const updateMutation = useUpdateRolePermissions(roleName);

    // Local grant state: permission name → isGranted
    const [grants, setGrants] = useState<Map<string, boolean>>(new Map());
    const [initialized, setInitialized] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [filterText, setFilterText] = useState('');
    const debouncedFilter = useDebounce(filterText, 200);

    // The server's saved state as a grant map — the init seed and what Cancel reverts to.
    const seedFromServer = useCallback(() => {
      if (!data?.groups) return;
      const initial = new Map<string, boolean>();
      for (const group of data.groups) {
        for (const perm of group.permissions) {
          initial.set(perm.name, perm.isGranted);
        }
      }
      setGrants(initial);
      setInitialized(true);
    }, [data]);

    // Initialize from server data
    useEffect(() => {
      seedFromServer();
    }, [seedFromServer]);

    // Build parent→children map for cascade logic
    const childrenMap = useMemo(() => {
      const map = new Map<string, string[]>();
      if (!data?.groups) return map;
      for (const group of data.groups) {
        for (const perm of group.permissions) {
          if (perm.parentName) {
            const siblings = map.get(perm.parentName) || [];
            siblings.push(perm.name);
            map.set(perm.parentName, siblings);
          }
        }
      }
      return map;
    }, [data]);

    // Compute which permissions match the filter (null = no filter active)
    const filterResult = useMemo(() => {
      const query = debouncedFilter.trim().toLowerCase();
      if (!query || !data?.groups) return null;

      const matchingPerms = new Set<string>();   // permissions whose displayName matches
      const visiblePerms = new Set<string>();     // matchingPerms + ancestors
      const visibleGroups = new Set<string>();

      for (const group of data.groups) {
        const groupMatches = group.displayName.toLowerCase().includes(query);
        if (groupMatches) {
          // Entire group matches — show all its permissions
          visibleGroups.add(group.name);
          for (const p of group.permissions) {
            matchingPerms.add(p.name);
            visiblePerms.add(p.name);
          }
          continue;
        }

        // Check individual permissions
        for (const perm of group.permissions) {
          if (perm.displayName.toLowerCase().includes(query)) {
            matchingPerms.add(perm.name);
            visiblePerms.add(perm.name);
            // Walk up to include ancestors
            let current = perm;
            while (current.parentName) {
              visiblePerms.add(current.parentName);
              const parent = group.permissions.find((p) => p.name === current.parentName);
              if (!parent) break;
              current = parent;
            }
            visibleGroups.add(group.name);
          }
        }
      }

      return { matchingPerms, visiblePerms, visibleGroups };
    }, [debouncedFilter, data]);

    // Permissions whose local grant differs from the server's — the pending change set. The
    // overview marks these yellow (app-standard "not saved yet") until the save lands.
    const unsavedPerms = useMemo(() => {
      const unsaved = new Set<string>();
      if (!data?.groups || !initialized) return unsaved;
      for (const group of data.groups) {
        for (const perm of group.permissions) {
          if (grants.get(perm.name) !== perm.isGranted) unsaved.add(perm.name);
        }
      }
      return unsaved;
    }, [data, grants, initialized]);

    const hasChanges = unsavedPerms.size > 0;

    useEffect(() => {
      onDirtyChange?.(hasChanges);
    }, [hasChanges, onDirtyChange]);

    // Cascade: when toggling a parent, toggle all descendants
    const togglePermission = useCallback(
      (permName: string, checked: boolean) => {
        setGrants((prev) => {
          const next = new Map(prev);
          next.set(permName, checked);

          const cascade = (parent: string) => {
            const children = childrenMap.get(parent) || [];
            for (const child of children) {
              next.set(child, checked);
              cascade(child);
            }
          };
          cascade(permName);

          return next;
        });
      },
      [childrenMap],
    );

    const toggleGroupAll = useCallback(
      (group: PermissionGroup, checked: boolean) => {
        setGrants((prev) => {
          const next = new Map(prev);
          for (const perm of group.permissions) {
            next.set(perm.name, checked);
          }
          return next;
        });
      },
      [],
    );

    const toggleGroupExpanded = useCallback((groupName: string) => {
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(groupName)) {
          next.delete(groupName);
        } else {
          next.add(groupName);
        }
        return next;
      });
    }, []);

    // A successful save invalidates the permissions query; the refetched server state re-seeds
    // the grants, which clears the unsaved marks and flips hasChanges off — no banner needed.
    const handleSave = useCallback(async () => {
      const permissions: PermissionGrant[] = [];
      for (const [name, isGranted] of grants) {
        permissions.push({ name, isGranted });
      }
      await updateMutation.mutateAsync({ permissions });
    }, [grants, updateMutation]);

    useImperativeHandle(ref, () => ({
      save: handleSave,
      revert: seedFromServer,
      hasChanges,
    }), [handleSave, seedFromServer, hasChanges]);

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">Failed to load permissions: {error.message}</Alert>;
    }

    if (!data?.groups || data.groups.length === 0) {
      return <Alert severity="info">No permissions available</Alert>;
    }

    const filteredGroups = filterResult
      ? data.groups.filter((g) => filterResult.visibleGroups.has(g.name))
      : data.groups;

    return (
      <Box>
        {/* Editing tree on the left; a live granted-only mirror on the right (overview of what
            the role actually holds — it follows every click, saved or not). */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Box sx={{ flex: '2 1 480px', minWidth: 360 }}>
            <TextField
              size="small"
              placeholder="Filter permissions..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              fullWidth
              sx={{ mb: 1.5 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: filterText ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setFilterText('')} edge="end">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
            {filteredGroups.length === 0 && debouncedFilter && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No permissions match "{debouncedFilter}"
              </Typography>
            )}
            {filteredGroups.map((group) => (
              <PermissionGroupSection
                key={group.name}
                group={group}
                grants={grants}
                childrenMap={childrenMap}
                expanded={expandedGroups.has(group.name) || !!filterResult}
                onToggleExpanded={() => toggleGroupExpanded(group.name)}
                onToggle={togglePermission}
                onToggleGroupAll={toggleGroupAll}
                visiblePerms={filterResult?.visiblePerms ?? null}
                matchingPerms={filterResult?.matchingPerms ?? null}
                filterQuery={filterResult ? debouncedFilter.trim().toLowerCase() : null}
              />
            ))}
          </Box>
          <Box
            sx={{
              flex: '1 1 300px',
              minWidth: 260,
              // Follows the reader down the long editing tree; degrades to a plain column
              // where no ancestor scrolls.
              position: 'sticky',
              top: 0,
              alignSelf: 'flex-start',
              maxHeight: '85vh',
              overflow: 'auto',
              pl: 2,
              borderLeft: 1,
              borderColor: 'divider',
            }}
          >
            <GrantedPermissionsOverview
              groups={data.groups}
              grants={grants}
              childrenMap={childrenMap}
              unsavedPerms={unsavedPerms}
            />
          </Box>
        </Box>
      </Box>
    );
  },
);

PermissionTree.displayName = 'PermissionTree';

// --- Permission Group Section ---

interface PermissionGroupSectionProps {
  group: PermissionGroup;
  grants: Map<string, boolean>;
  childrenMap: Map<string, string[]>;
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggle: (permName: string, checked: boolean) => void;
  onToggleGroupAll: (group: PermissionGroup, checked: boolean) => void;
  visiblePerms: Set<string> | null;
  matchingPerms: Set<string> | null;
  filterQuery: string | null;
}

function PermissionGroupSection({
  group,
  grants,
  childrenMap,
  expanded,
  onToggleExpanded,
  onToggle,
  onToggleGroupAll,
  visiblePerms,
  matchingPerms,
  filterQuery,
}: PermissionGroupSectionProps) {
  const allGranted = group.permissions.every((p) => grants.get(p.name));
  const noneGranted = group.permissions.every((p) => !grants.get(p.name));
  const grantedCount = group.permissions.filter((p) => grants.get(p.name)).length;

  const rootPerms = group.permissions.filter((p) => !p.parentName);

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          py: 0.5,
          px: 0.5,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={onToggleExpanded}
      >
        <IconButton size="small" sx={{ p: 0 }}>
          <ExpandMore
            sx={{
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 150ms',
              fontSize: '1.25rem',
            }}
          />
        </IconButton>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {group.displayName}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          {grantedCount}/{group.permissions.length}
        </Typography>
        {expanded && (
          <>
            <Button
              size="small"
              variant="text"
              onClick={(e) => { e.stopPropagation(); onToggleGroupAll(group, true); }}
              disabled={allGranted}
              sx={{ minWidth: 'auto', fontSize: '0.7rem', py: 0, px: 0.5 }}
            >
              All
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={(e) => { e.stopPropagation(); onToggleGroupAll(group, false); }}
              disabled={noneGranted}
              sx={{ minWidth: 'auto', fontSize: '0.7rem', py: 0, px: 0.5 }}
            >
              None
            </Button>
          </>
        )}
      </Box>
      <Collapse in={expanded}>
        <Divider sx={{ mb: 0.5 }} />
        {rootPerms
          .filter((p) => !visiblePerms || visiblePerms.has(p.name))
          .map((perm) => (
            <PermissionNode
              key={perm.name}
              permission={perm}
              grants={grants}
              childrenMap={childrenMap}
              allPermissions={group.permissions}
              onToggle={onToggle}
              depth={0}
              visiblePerms={visiblePerms}
              matchingPerms={matchingPerms}
              filterQuery={filterQuery}
            />
          ))}
      </Collapse>
    </Box>
  );
}

// --- Permission Node (recursive) ---

interface PermissionNodeProps {
  permission: Permission;
  grants: Map<string, boolean>;
  childrenMap: Map<string, string[]>;
  allPermissions: Permission[];
  onToggle: (permName: string, checked: boolean) => void;
  depth: number;
  visiblePerms: Set<string> | null;
  matchingPerms: Set<string> | null;
  filterQuery: string | null;
}

function HighlightText({ text, query }: { text: string; query: string | null }) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="span" sx={{ bgcolor: 'warning.light', borderRadius: 0.5, px: 0.25 }}>
        {text.slice(idx, idx + query.length)}
      </Box>
      {text.slice(idx + query.length)}
    </>
  );
}

function PermissionNode({
  permission,
  grants,
  childrenMap,
  allPermissions,
  onToggle,
  depth,
  visiblePerms,
  matchingPerms,
  filterQuery,
}: PermissionNodeProps) {
  const isGranted = grants.get(permission.name) ?? false;
  const children = childrenMap.get(permission.name) || [];
  const childPerms = (
    children
      .map((name) => allPermissions.find((p) => p.name === name))
      .filter(Boolean) as Permission[]
  ).filter((p) => !visiblePerms || visiblePerms.has(p.name));

  const isMatch = matchingPerms?.has(permission.name) ?? false;

  return (
    <Box sx={{ ml: depth * 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.25,
          '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
          ...(isMatch && filterQuery ? { bgcolor: 'action.selected', borderRadius: 1 } : {}),
        }}
      >
        <Checkbox
          checked={isGranted}
          onChange={(_, checked) => onToggle(permission.name, checked)}
          size="small"
          sx={{ p: 0.25 }}
          slotProps={{ input: { 'aria-label': permission.displayName } }}
        />
        <Typography variant="body2" sx={{ ml: 0.5 }}>
          <HighlightText text={permission.displayName} query={isMatch ? filterQuery : null} />
        </Typography>
      </Box>

      {childPerms.map((child) => (
        <PermissionNode
          key={child.name}
          permission={child}
          grants={grants}
          childrenMap={childrenMap}
          allPermissions={allPermissions}
          onToggle={onToggle}
          depth={depth + 1}
          visiblePerms={visiblePerms}
          matchingPerms={matchingPerms}
          filterQuery={filterQuery}
        />
      ))}
    </Box>
  );
}
